import { pgTable, serial, integer, text, timestamp, numeric, jsonb, date, uniqueIndex, index, boolean } from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";

// ---------------------------------------------------------------------------
// Configuration (employer decisions, effective-dated where they matter)
// ---------------------------------------------------------------------------

// Reporting year status. Administr never files with the IRS, so status stops at
// "finalized"; "filed externally" is user-entered recordkeeping only.
export const ACA_YEAR_STATUSES = ["not_started", "in_progress", "needs_attention", "ready", "finalized"] as const;
export type AcaYearStatus = (typeof ACA_YEAR_STATUSES)[number];

export const acaReportingYears = pgTable("aca_reporting_years", {
  id: serial("id").primaryKey(),
  clientId: integer("client_id").notNull(),
  taxYear: integer("tax_year").notNull(),
  status: text("status").notNull().default("not_started"),
  isLocked: boolean("is_locked").default(false),
  finalizedAt: timestamp("finalized_at"),
  finalizedByUserId: text("finalized_by_user_id"),
  finalSnapshotId: integer("final_snapshot_id"),
  filedExternallyAt: date("filed_externally_at"),
  filedExternallyNotes: text("filed_externally_notes"),
  filedAt: timestamp("filed_at"), // legacy
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (t) => ({
  uniq: uniqueIndex("aca_reporting_year_unique").on(t.clientId, t.taxYear),
}));

// Measurement-method policy per client + year (employer decision).
// Statutory values (30 hrs/wk, 130 hrs/mo, affordability %, FPL) live in
// shared/aca/statutory.ts and are NOT stored here.
export const acaPolicies = pgTable("aca_policies", {
  id: serial("id").primaryKey(),
  clientId: integer("client_id").notNull(),
  year: integer("year").notNull(),
  method: text("method").notNull().default("monthly"), // monthly | look-back
  // Look-back: actual dates of the standard measurement / admin / stability
  // periods that govern this tax year's stability period.
  measurementStart: date("measurement_start"),
  measurementEnd: date("measurement_end"),
  adminDays: integer("admin_days").default(0),
  stabilityStart: date("stability_start"),
  stabilityEnd: date("stability_end"),
  // Look-back: durations used to roll periods forward and to build
  // employee-specific initial measurement periods for variable-hour/seasonal
  // /part-time new hires.
  standardMeasurementMonths: integer("standard_measurement_months").default(12),
  initialMeasurementMonths: integer("initial_measurement_months").default(12),
  initialAdminDays: integer("initial_admin_days").default(30),
  stabilityMonths: integer("stability_months").default(12),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (t) => ({
  uniq: uniqueIndex("aca_policies_client_year_idx").on(t.clientId, t.year),
}));

// Safe-harbor assignment (employer decision). One default policy per year plus
// optional category policies (employee class / compensation type / employment
// type) that must be applied consistently within the category.
export const acaAffordabilityPolicies = pgTable("aca_affordability_policies", {
  id: serial("id").primaryKey(),
  clientId: integer("client_id").notNull(),
  taxYear: integer("tax_year").notNull(),
  name: text("name").notNull().default("General"),
  safeHarbor: text("safe_harbor").notNull().default("fpl"), // fpl | rate_of_pay | w2
  config: jsonb("config").default({}), // legacy; no longer read
  // { employeeClassIds?: number[], compensationTypes?: ("hourly"|"salary")[], employmentTypes?: string[] }
  segmentation: jsonb("segmentation").default({}),
  isDefault: boolean("is_default").default(false),
  createdByUserId: text("created_by_user_id"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// Advanced override layer for the employee required contribution. Default
// derivation is automatic (lowest-cost MV self-only plan the employee is
// eligible for, minus employer contribution); a rule here overrides it for
// the months/segment it covers and must carry a reason.
export const acaLine15CostRules = pgTable("aca_line15_cost_rules", {
  id: serial("id").primaryKey(),
  clientId: integer("client_id").notNull(),
  taxYear: integer("tax_year").notNull(),
  name: text("name").default("Override"),
  effectiveStartMonth: integer("effective_start_month").notNull().default(1),
  effectiveEndMonth: integer("effective_end_month").notNull().default(12),
  sourceType: text("source_type").notNull().default("manual_override"), // manual_override | benefits_plan (legacy)
  planId: integer("plan_id"),
  employerContributionStrategyId: integer("employer_contribution_strategy_id"),
  line15EmployeeCost: numeric("line15_employee_cost", { precision: 10, scale: 2 }),
  overrideReason: text("override_reason"),
  segmentation: jsonb("segmentation").default({}),
  createdByUserId: text("created_by_user_id"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// Client-level ACA settings: genuine preferences + audited ALE override.
export const acaSettings = pgTable("aca_settings", {
  id: serial("id").primaryKey(),
  clientId: integer("client_id").notNull(),
  aleStartDate: date("ale_start_date"),
  // Permitted employer choice for 1094-C Part III column (c) total employee
  // count: first_day | last_day | twelfth_day (of each month).
  employeeCountDay: text("employee_count_day").default("first_day"),
  isAggregatedAleGroup: boolean("is_aggregated_ale_group").default(false),
  // Audited ALE override (advanced).
  aleOverride: boolean("ale_override"),
  aleOverrideReason: text("ale_override_reason"),
  aleOverrideByUserId: text("ale_override_by_user_id"),
  aleOverrideAt: timestamp("ale_override_at"),
  // Employee-facing preferences.
  allowEmployeeViewForms: boolean("allow_employee_view_forms").default(true),
  hideEmployee1095c: boolean("hide_employee_1095c").default(false),
  showHoursToEmployees: boolean("show_hours_to_employees").default(false),
  // Notifications.
  notifyDeadlines: boolean("notify_deadlines").default(true),
  notifyExceptions: boolean("notify_exceptions").default(true),
  reminderDays: integer("reminder_days").default(30),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (t) => ({
  uniq: uniqueIndex("aca_settings_client_unique").on(t.clientId),
}));

// Related employers in the aggregated / controlled group (1094-C Part IV).
export const acaAleGroupMembers = pgTable("aca_ale_group_members", {
  id: serial("id").primaryKey(),
  clientId: integer("client_id").notNull(),
  memberName: text("member_name").notNull(),
  memberEin: text("member_ein"),
  memberClientId: integer("member_client_id"), // when the related employer is also an Administr client
  effectiveFrom: date("effective_from"),
  effectiveTo: date("effective_to"),
  createdAt: timestamp("created_at").defaultNow(),
}, (t) => ({
  byClient: index("aca_ale_group_members_client_idx").on(t.clientId),
}));

// Superadmin-only override of the code-owned statutory table
// (shared/aca/statutory.ts) for a plan-year-start year not yet in code.
// affordability_percent is a PERCENT (9.02), fpl_annual_single in dollars.
export const acaConstants = pgTable("aca_constants", {
  id: serial("id").primaryKey(),
  year: integer("year").notNull(),
  affordabilityPercent: numeric("affordability_percent", { precision: 6, scale: 4 }).notNull(),
  fplAnnualSingle: integer("fpl_annual_single").notNull(),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (t) => ({
  uniq: uniqueIndex("aca_constants_year_idx").on(t.year),
}));

// ---------------------------------------------------------------------------
// Source data owned by ACA: monthly hours of service
// ---------------------------------------------------------------------------

export const acaEmployeeMonthlies = pgTable("aca_employee_monthlies", {
  id: serial("id").primaryKey(),
  clientId: integer("client_id").notNull(),
  employeeId: integer("employee_id").notNull(),
  year: integer("year").notNull(),
  month: integer("month").notNull(), // 1-12
  hours: numeric("hours", { precision: 8, scale: 2 }).notNull(),
  source: text("source").notNull().default("manual"), // manual | import | payroll | schedule_estimate
  updatedByUserId: text("updated_by_user_id"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (t) => ({
  uniq: uniqueIndex("aca_emp_month_unique").on(t.employeeId, t.year, t.month),
}));

// ---------------------------------------------------------------------------
// Engine output: employee x month results, persisted per snapshot
// ---------------------------------------------------------------------------

export const acaYearSnapshots = pgTable("aca_year_snapshots", {
  id: serial("id").primaryKey(),
  clientId: integer("client_id").notNull(),
  taxYear: integer("tax_year").notNull(),
  version: integer("version").notNull().default(1),
  status: text("status").notNull().default("draft"), // draft | final
  computedAt: timestamp("computed_at").defaultNow(),
  computedByUserId: text("computed_by_user_id"),
  // ALE + 1094-C aggregates and the configuration used, so a finalized year
  // never changes when HR/benefits data changes later.
  summary: jsonb("summary").notNull().default({}),
  configUsed: jsonb("config_used").notNull().default({}),
  exceptions: jsonb("exceptions").notNull().default([]),
  engineVersion: text("engine_version").notNull().default("1"),
}, (t) => ({
  uniq: uniqueIndex("aca_year_snapshots_unique").on(t.clientId, t.taxYear, t.version),
}));

export const acaEmployeeMonthResults = pgTable("aca_employee_month_results", {
  id: serial("id").primaryKey(),
  snapshotId: integer("snapshot_id").notNull(),
  clientId: integer("client_id").notNull(),
  taxYear: integer("tax_year").notNull(),
  employeeId: integer("employee_id").notNull(),
  month: integer("month").notNull(),
  employed: boolean("employed").notNull().default(false),
  acaStatus: text("aca_status"), // full_time | non_full_time | measuring | lnap | not_employed | unknown
  hours: numeric("hours", { precision: 8, scale: 2 }),
  hoursSource: text("hours_source"),
  lnapReason: text("lnap_reason"),
  offered: boolean("offered"),
  offerPlanId: integer("offer_plan_id"),
  line14: text("line14"),
  line15: numeric("line15", { precision: 10, scale: 2 }),
  line15Source: text("line15_source"),
  line16: text("line16"),
  enrolled: boolean("enrolled"),
  enrolledPlanId: integer("enrolled_plan_id"),
  safeHarbor: text("safe_harbor"),
  threshold: numeric("threshold", { precision: 10, scale: 2 }),
  affordability: text("affordability"), // affordable | not_affordable | pending_data | not_applicable
  inputs: jsonb("inputs").notNull().default({}),
  explanation: jsonb("explanation").notNull().default([]),
  exceptions: jsonb("exceptions").notNull().default([]),
}, (t) => ({
  uniq: uniqueIndex("aca_employee_month_results_unique").on(t.snapshotId, t.employeeId, t.month),
  byEmployee: index("aca_employee_month_results_emp_idx").on(t.clientId, t.taxYear, t.employeeId),
}));

// Audited manual overrides of engine output (Line 14/15/16, plan start month,
// 1094-C counts). Applied on top of the computed result at read time.
export const acaOverrides = pgTable("aca_overrides", {
  id: serial("id").primaryKey(),
  clientId: integer("client_id").notNull(),
  taxYear: integer("tax_year").notNull(),
  employeeId: integer("employee_id"), // null = 1094-C level
  month: integer("month"), // null = whole year
  field: text("field").notNull(), // line14 | line15 | line16 | plan_start_month | ft_count | total_count | mec_offered | ale
  priorValue: text("prior_value"),
  newValue: text("new_value"),
  reason: text("reason").notNull(),
  createdByUserId: text("created_by_user_id"),
  createdAt: timestamp("created_at").defaultNow(),
  revokedAt: timestamp("revoked_at"),
  revokedByUserId: text("revoked_by_user_id"),
}, (t) => ({
  byYear: index("aca_overrides_year_idx").on(t.clientId, t.taxYear),
}));

// ---------------------------------------------------------------------------
// Workforce snapshots: a frozen copy of the roster as of a date, for data
// history and as the basis of the following year's ALE determination.
// ---------------------------------------------------------------------------

export const acaWorkforceSnapshots = pgTable("aca_workforce_snapshots", {
  id: serial("id").primaryKey(),
  clientId: integer("client_id").notNull(),
  snapshotYear: integer("snapshot_year").notNull(), // calendar year captured
  asOf: date("as_of").notNull(), // last day covered (Dec 31 for year-end)
  kind: text("kind").notNull().default("manual"), // year_end | manual
  status: text("status").notNull().default("frozen"), // frozen | superseded
  employeeCount: integer("employee_count").notNull().default(0),
  // Same shape the engine's ALE uses: monthly FT, FTE, totals, average, isAle, estimated.
  summary: jsonb("summary").notNull().default({}),
  notes: text("notes"),
  takenAt: timestamp("taken_at").defaultNow(),
  takenByUserId: text("taken_by_user_id"),
  supersededAt: timestamp("superseded_at"),
  supersededByUserId: text("superseded_by_user_id"),
  supersedeReason: text("supersede_reason"),
  engineVersion: text("engine_version").notNull().default("1"),
}, (t) => ({
  byClientYear: index("aca_workforce_snapshots_client_year_idx").on(t.clientId, t.snapshotYear),
}));

export const acaWorkforceSnapshotEmployees = pgTable("aca_workforce_snapshot_employees", {
  id: serial("id").primaryKey(),
  snapshotId: integer("snapshot_id").notNull(),
  employeeId: integer("employee_id").notNull(),
  firstName: text("first_name"),
  lastName: text("last_name"),
  employeeNumber: text("employee_number"),
  status: text("status"),
  employmentType: text("employment_type"),
  employeeClassId: integer("employee_class_id"),
  locationId: integer("location_id"),
  state: text("state"),
  hireDate: date("hire_date"),
  terminationDate: date("termination_date"),
  periods: jsonb("periods").notNull().default([]), // [{start,end}]
  scheduledHoursPerWeek: numeric("scheduled_hours_per_week", { precision: 6, scale: 2 }),
  compensationType: text("compensation_type"),
  compensationAmount: numeric("compensation_amount", { precision: 12, scale: 2 }),
  monthlyHours: jsonb("monthly_hours").notNull().default({}), // {1: 173.3, ...}
  hoursSource: jsonb("hours_source").notNull().default({}), // {1: "monthly_hours"|"schedule_estimate"|"none"}
  monthlyStatus: jsonb("monthly_status").notNull().default({}), // {1: "full_time"|"non_full_time"|"not_employed"|"unknown"}
  monthsEmployed: integer("months_employed").notNull().default(0),
  monthsFullTime: integer("months_full_time").notNull().default(0),
}, (t) => ({
  bySnapshot: index("aca_workforce_snapshot_employees_snapshot_idx").on(t.snapshotId),
  uniq: uniqueIndex("aca_workforce_snapshot_employees_unique").on(t.snapshotId, t.employeeId),
}));

// ---------------------------------------------------------------------------
// Forms (versioned outputs of a snapshot)
// ---------------------------------------------------------------------------

export const aca1094c = pgTable("aca_1094c", {
  id: serial("id").primaryKey(),
  clientId: integer("client_id").notNull(),
  year: integer("year").notNull(),
  snapshotId: integer("snapshot_id"),
  version: integer("version").notNull().default(1),
  authoritative: text("authoritative").default("yes"),
  isAle: boolean("is_ale"),
  isAggregatedGroup: boolean("is_aggregated_group").default(false),
  totalForms1095c: integer("total_forms_1095c").default(0),
  monthlyFte: jsonb("monthly_fte"), // legacy: {1: n, ...}
  monthlyFtCount: jsonb("monthly_ft_count"), // {1: n, ...} FT employees excluding LNAP
  monthlyTotalCount: jsonb("monthly_total_count"), // {1: n, ...}
  monthlyMecOffered: jsonb("monthly_mec_offered"), // {1: bool, ...}
  data: jsonb("data"),
  generatedAt: timestamp("generated_at").defaultNow(),
}, (t) => ({
  uniq: uniqueIndex("aca_1094c_client_year_version_idx").on(t.clientId, t.year, t.version),
}));

export const aca1095cRecords = pgTable("aca_1095c_records", {
  id: serial("id").primaryKey(),
  clientId: integer("client_id").notNull(),
  employeeId: integer("employee_id").notNull(),
  year: integer("year").notNull(),
  snapshotId: integer("snapshot_id"),
  version: integer("version").notNull().default(1),
  isCorrection: boolean("is_correction").default(false),
  requiredReason: text("required_reason"), // full_time | self_insured_enrolled
  planStartMonth: text("plan_start_month"), // "00".."12"
  line14: jsonb("line14"), // {1:"1E",...}
  line15: jsonb("line15"), // {1:123.45,...}
  line16: jsonb("line16"), // {1:"2C",...}
  part3: jsonb("part3"), // covered individuals for self-insured coverage
  status: text("status").default("generated"),
  generatedAt: timestamp("generated_at").defaultNow(),
}, (t) => ({
  uniq: uniqueIndex("aca_1095c_unique").on(t.employeeId, t.year, t.version),
}));

export const acaGeneratedForms = pgTable("aca_generated_forms", {
  id: serial("id").primaryKey(),
  clientId: integer("client_id").notNull(),
  taxYear: integer("tax_year").notNull(),
  formType: text("form_type").notNull(), // 1095c | 1094c
  employeeId: integer("employee_id"),
  snapshotId: integer("snapshot_id"),
  version: integer("version").notNull().default(1),
  isCorrected: boolean("is_corrected").default(false),
  pdfUrl: text("pdf_url"),
  s3Key: text("s3_key"),
  documentId: integer("document_id"),
  generatedAt: timestamp("generated_at").defaultNow(),
  generatedByUserId: text("generated_by_user_id"),
});

// ---------------------------------------------------------------------------
// Insert schemas + types
// ---------------------------------------------------------------------------

export const insertAcaPolicySchema = createInsertSchema(acaPolicies).omit({ id: true, createdAt: true, updatedAt: true });
export const upsertAcaConstantsSchema = createInsertSchema(acaConstants).omit({ id: true, createdAt: true, updatedAt: true });
export const insertAcaEmployeeMonthlySchema = createInsertSchema(acaEmployeeMonthlies).omit({ id: true, createdAt: true, updatedAt: true });
export const insertAca1094cSchema = createInsertSchema(aca1094c).omit({ id: true });
export const insertAca1095cRecordSchema = createInsertSchema(aca1095cRecords).omit({ id: true });
export const insertAcaReportingYearSchema = createInsertSchema(acaReportingYears).omit({ id: true, createdAt: true, updatedAt: true });
export const insertAcaAffordabilityPolicySchema = createInsertSchema(acaAffordabilityPolicies).omit({ id: true, createdAt: true, updatedAt: true });
export const insertAcaLine15CostRuleSchema = createInsertSchema(acaLine15CostRules).omit({ id: true, createdAt: true, updatedAt: true });
export const insertAcaGeneratedFormSchema = createInsertSchema(acaGeneratedForms).omit({ id: true });
export const insertAcaSettingsSchema = createInsertSchema(acaSettings).omit({ id: true, createdAt: true, updatedAt: true });
export const insertAcaAleGroupMemberSchema = createInsertSchema(acaAleGroupMembers).omit({ id: true, createdAt: true });
export const insertAcaOverrideSchema = createInsertSchema(acaOverrides).omit({ id: true, createdAt: true, revokedAt: true, revokedByUserId: true });

export type AcaPolicy = typeof acaPolicies.$inferSelect;
export type InsertAcaPolicy = z.infer<typeof insertAcaPolicySchema>;
export type AcaConstants = typeof acaConstants.$inferSelect;
export type UpsertAcaConstants = z.infer<typeof upsertAcaConstantsSchema>;
export type AcaEmployeeMonthly = typeof acaEmployeeMonthlies.$inferSelect;
export type InsertAcaEmployeeMonthly = z.infer<typeof insertAcaEmployeeMonthlySchema>;
export type Aca1094c = typeof aca1094c.$inferSelect;
export type InsertAca1094c = z.infer<typeof insertAca1094cSchema>;
export type Aca1095cRecord = typeof aca1095cRecords.$inferSelect;
export type InsertAca1095cRecord = z.infer<typeof insertAca1095cRecordSchema>;
export type AcaReportingYear = typeof acaReportingYears.$inferSelect;
export type InsertAcaReportingYear = z.infer<typeof insertAcaReportingYearSchema>;
export type AcaAffordabilityPolicy = typeof acaAffordabilityPolicies.$inferSelect;
export type InsertAcaAffordabilityPolicy = z.infer<typeof insertAcaAffordabilityPolicySchema>;
export type AcaLine15CostRule = typeof acaLine15CostRules.$inferSelect;
export type InsertAcaLine15CostRule = z.infer<typeof insertAcaLine15CostRuleSchema>;
export type AcaGeneratedForm = typeof acaGeneratedForms.$inferSelect;
export type InsertAcaGeneratedForm = z.infer<typeof insertAcaGeneratedFormSchema>;
export type AcaSettingsType = typeof acaSettings.$inferSelect;
export type InsertAcaSettings = z.infer<typeof insertAcaSettingsSchema>;
export type AcaAleGroupMember = typeof acaAleGroupMembers.$inferSelect;
export type AcaYearSnapshot = typeof acaYearSnapshots.$inferSelect;
export type AcaEmployeeMonthResult = typeof acaEmployeeMonthResults.$inferSelect;
export type AcaOverride = typeof acaOverrides.$inferSelect;
export type AcaWorkforceSnapshot = typeof acaWorkforceSnapshots.$inferSelect;
export type AcaWorkforceSnapshotEmployee = typeof acaWorkforceSnapshotEmployees.$inferSelect;
export type InsertAcaOverride = z.infer<typeof insertAcaOverrideSchema>;
